import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  CardTitle,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import classnames from "classnames";
import moment from "moment";
// Import components
import Breadcrumb from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";

// Redux and Actions
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import withRouter from "components/Common/withRouter";
import { getUsers, getUserProfile } from "store/account/actions";
import { ROLES } from "common/localData";
import { editUserProfile, editChildUser, deleteChildUser, createChildUser, assignUserToLists, getUserAssignedLists, apiErrorMessage } from "../../helpers/api_helper";
import {
  getTenantConfig,
  listCompanyDepartments,
  createDepartment,
  renameDepartment,
  deleteDepartment,
} from "../../helpers/account_helper";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Swal from "sweetalert2";
import ChangePasswordModal from "./ChangePassword";
import UserUsageChart from './unitsChart';

const UNASSIGNED_DEPARTMENT = "__none__";

const asOption = (d) => ({ value: d, label: d });
const ROLE_OPTIONS = ROLES.map((r) => ({ value: r.value, label: r.text }));

// Department names run long ("Masinga Hydroelectric Power Station, Machakos") — let the
// menu size to its content instead of wrapping inside the control's width.
const DEPT_SELECT_STYLES = {
  menu: (base) => ({ ...base, zIndex: 5, width: "max-content", minWidth: "100%", maxWidth: 460 }),
  option: (base) => ({ ...base, whiteSpace: "nowrap" }),
};

// Departments are free-form strings, not a lookup table, so this is a Creatable —
// typing a name that doesn't exist yet offers it as a new one.
const DepartmentField = ({ id, value, departments, onChange }) => (
  <FormGroup>
    <Label for={id}>Department</Label>
    <CreatableSelect
      inputId={id}
      isClearable
      options={departments.map(asOption)}
      value={value ? asOption(value) : null}
      onChange={(opt) => onChange(opt?.value || "")}
      placeholder="Search or type a department…"
      formatCreateLabel={(input) => `Add new department "${input}"`}
      styles={DEPT_SELECT_STYLES}
    />
  </FormGroup>
);

// Add / rename / merge / delete the tenant's departments. Renaming onto a name that
// already exists is how you merge two of them.
const ManageDepartmentsModal = ({ isOpen, toggle, companyId, departments, onChanged }) => {
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
      setEditing(null);
      setNewName("");
      Swal.fire("Success", successMessage, "success");
    } catch (error) {
      Swal.fire("Error", apiErrorMessage(error, "That didn't work. Please try again."), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (name) => {
    const { value: choice, isConfirmed } = await Swal.fire({
      title: `Delete "${name}"?`,
      input: "select",
      inputOptions: {
        "": "Clear the department on its members",
        ...Object.fromEntries(departments.filter((d) => d !== name).map((d) => [d, `Move members to ${d}`])),
      },
      inputValue: "",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!isConfirmed) return;
    run(() => deleteDepartment(companyId, name, choice ?? ""), `"${name}" deleted.`);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Manage Departments</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="new-department">Add a department</Label>
          <div className="d-flex gap-2">
            <Input
              id="new-department"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Upper Tana"
            />
            <Button
              color="primary"
              disabled={!newName.trim() || busy}
              onClick={() => run(() => createDepartment(companyId, newName.trim()), `"${newName.trim()}" added.`)}
            >
              Add
            </Button>
          </div>
        </FormGroup>
        <hr />
        <p className="text-muted" style={{ fontSize: "0.85rem" }}>
          Renaming a department to a name that already exists merges the two. SMS history keeps
          the department it was sent under.
        </p>
        {departments.length === 0 && <p className="text-muted mb-0">No departments yet.</p>}
        {departments.map((d) => (
          <div key={d} className="d-flex align-items-center gap-2 py-2 border-bottom">
            {editing === d ? (
              <>
                <Input value={editValue} onChange={(e) => setEditValue(e.target.value)} />
                <Button
                  color="primary"
                  size="sm"
                  disabled={!editValue.trim() || busy}
                  onClick={() =>
                    run(
                      () => renameDepartment(companyId, d, editValue.trim()),
                      departments.some((x) => x.toLowerCase() === editValue.trim().toLowerCase())
                        ? `"${d}" merged into "${editValue.trim()}".`
                        : `Renamed to "${editValue.trim()}".`
                    )
                  }
                >
                  Save
                </Button>
                <Button color="secondary" size="sm" onClick={() => setEditing(null)} disabled={busy}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <span className="flex-grow-1">{d}</span>
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={() => { setEditing(d); setEditValue(d); }}
                  disabled={busy}
                >
                  Rename
                </Button>
                <span
                  className="text-danger"
                  style={{ cursor: busy ? "default" : "pointer" }}
                  onClick={() => !busy && handleDelete(d)}
                  title="Delete department"
                >
                  <i className="mdi mdi-trash-can-outline font-size-18" />
                </span>
              </>
            )}
          </div>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={busy}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

const UserProfile = () => {
  // Meta title
  document.title = "Profile | Contacts";

  const dispatch = useDispatch();

  const { contactLists: availableContactLists } = useSelector(state => state.ContactList);

  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 0,
    sortBy: 'id',
    ascendingSort: false,
  });
  const [userSearch, setUserSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [activeTab, setActiveTab] = useState("overview");


  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone: "",
    short_code: '',
    role_id: "",
    department: "",
  });

  // Departments are opt-in per tenant. Show the field when the tenant opted in OR
  // already has departments in use — never as an empty picker.
  const [deptScoping, setDeptScoping] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [manageDeptModal, setManageDeptModal] = useState(false);
  const deptEnabled = deptScoping || departments.length > 0;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value });
    setCurrentUser({ ...currentUser, [name]: value });
  };

  const [modal, setModal] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [unitModal, setUnitModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);

  const [editFormData, setEditFormData] = useState({
    name: "",
    phone: "",
  });

  const [editUserModal, setEditUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    user_id: "",
    name: "",
    phone: "",
    short_code: '',
    role_id: "",
  });

  const toggleEditUserModal = () => setEditUserModal(!editUserModal);

  const [unitFormData, setUnitFormData] = useState({
    client_id: "1",
    new_allocated_units: "",
  });

  const toggleModal = () => setModal(!modal);

  const handleNewUser = () => {
    toggleModal();
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // Bonga stores first/last name separately and rejects a blank surname.
    if (String(newUser.name).trim().split(/\s+/).filter(Boolean).length < 2) {
      Swal.fire("Error", "Please enter both a first and last name.", "error");
      return;
    }
    if (deptEnabled && !String(newUser.department || "").trim()) {
      Swal.fire("Error", "Please choose a department.", "error");
      return;
    }
    if (!String(newUser.role_id || "").trim()) {
      Swal.fire("Error", "Please choose a role.", "error");
      return;
    }
    try {
      await createChildUser(user?.companyId, newUser);
      Swal.fire("Success", "New user created successfully!", "success");
      toggleModal();
      setNewUser({ name: "", email: "", phone: "", department: "", role_id: "" });
      dispatch(getUsers({ companyId: user?.companyId }));
      if (deptEnabled) refreshDepartments(user?.companyId);
    } catch (error) {
      Swal.fire("Error", apiErrorMessage(error, "Failed to create user. Please try again."), "error");
    }
  };

  // Seed the form from the loaded profile on open — it opened blank otherwise, so
  // saving wiped the name and phone.
  const toggleEditModal = () => {
    if (!editModal) {
      setEditFormData({
        name: [user?.firstName, user?.lastName].filter(Boolean).join(" "),
        phone: user?.phone || "",
      });
    }
    setEditModal(!editModal);
  };
  const toggleUnitModal = () => setUnitModal(!unitModal);
  const togglePasswordModal = () => setPasswordModal(!passwordModal);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (String(editFormData.name).trim().split(/\s+/).filter(Boolean).length < 2) {
      Swal.fire("Error", "Please enter both a first and last name.", "error");
      return;
    }
    try {
      await editUserProfile(user?.id, {
        ...editFormData,
        phone: editFormData.phone === user?.phone ? undefined : editFormData.phone,
      });
      Swal.fire("Success", "Profile updated successfully!", "success");
      toggleEditModal();
      dispatch(getUserProfile());
    } catch (error) {
      Swal.fire("Error", apiErrorMessage(error, "Failed to update profile. Please try again."), "error");
    }
  };

  const [assignModal, setAssignModal] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [allLists, setAllLists] = useState([]);
  const [assignedListIds, setAssignedListIds] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [assignedLists, setAssignedLists] = useState([]);

  const handleAssignContact = async (groupLeadUser) => {
    setAssigningUser(groupLeadUser);
    setAssignModal(true);
    setLoadingLists(true);

    try {
      // 1. Fetch assigned lists (with full data)
      const assignedRes = await getUserAssignedLists(groupLeadUser.id);
      const assignedLists = Array.isArray(assignedRes) ? assignedRes : (assignedRes?.data || []);

      // Store full objects
      setAssignedListIds(assignedLists.map(l => l.id));
      setSelectedListIds(assignedLists.map(l => l.id));

      // Optional: store full objects for display
      setAssignedLists(assignedLists);

      // 2. Use contact lists from Redux
      if (Array.isArray(availableContactLists) && availableContactLists.length > 0) {
        setAllLists(availableContactLists);
      } else {
        setAllLists([]);
        Swal.fire("Notice", "No contact lists available. Please add some first.", "info");
      }

    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load contact lists.", "error");
    } finally {
      setLoadingLists(false);
    }
  };

  const handleAssignSave = async () => {
    if (!assigningUser) return;

    const payload = {
      user_id: assigningUser.id.toString(),
      contact_list_id: selectedListIds.map(id => id.toString()),
    };

    try {
      await assignUserToLists(payload);
      Swal.fire("Success", "Contact lists assigned successfully to the Group Lead.", "success");
      setAssignModal(false);
      // dispatch(getUsers({ client_id: user.User.client_id }));
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to assign contact lists.", "error");
    }
  };

  // Columns for the Users Table
  const columns = useMemo(
    () => [
      // ID column hidden — bonga uses UUIDs (not the legacy small ints); not useful to the user.
      {
        header: "Created On",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (info) => {
          const dateValue = info.getValue();
          const formattedDate = dateValue ? moment(dateValue).format('YYYY MMM DD h:mm A') : '';
          return <span>{formattedDate}</span>;
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Staff Number",
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: "Role",
        accessorKey: "role_id",
        enableColumnFilter: false,
        cell: (info) => {
          const role_id = info?.getValue();
          return <span>{ROLES?.find(r => r?.value == role_id)?.text}</span>;
        },
      },
      {
        header: "Department",
        accessorKey: "department",
        enableColumnFilter: false,
      },
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
      },
      {
        header: "Actions",
        enableColumnFilter: false,
        cell: (info) => {
          const rowUser = info.row.original;
          // Non-admin members are the assignable population (KenGen has no "group lead"
          // role in Bonga — list access is the explicit per-user ACL). Only admins assign.
          const isGroupLead = user?.isCompanyAdmin && rowUser.role_id !== 1;

          return (
            <div className="d-flex gap-3 align-items-center">
              {/* Edit Icon - Always visible */}
              <span
                className="text-success"
                style={{ cursor: "pointer" }}
                onClick={() => handleEditUserClick(rowUser)}
                title="Edit User"
              >
                <i className="mdi mdi-pencil font-size-18" />
              </span>

              {/* Delete Icon - Always visible */}
              <span
                className="text-danger"
                style={{ cursor: "pointer" }}
                onClick={() => handleDeleteUser(rowUser.id)}
                title="Delete User"
              >
                <i className="mdi mdi-delete font-size-18" />
              </span>

              {isGroupLead && (
                <Button
                  size="sm"
                  color="info"
                  onClick={() => handleAssignContact(rowUser)}
                  className="d-flex align-items-center gap-1"
                  title="Assign this Group Lead as Contact"
                >
                  <i className="mdi mdi-account-check font-size-14" />
                  Assign Contact
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const ContactListProperties = createSelector(
    (state) => state.User,
    (user) => ({
      error: user.error,
      user: user.userProfile
    })
  );

  const {
    error: serError,
    user
  } = useSelector(ContactListProperties);

  // Only company admins may list/manage other users (users.read on the backend).
  const isAdmin = !!user?.isCompanyAdmin;

  useEffect(() => {
    if (user?.companyId && user?.isCompanyAdmin) dispatch(getUsers({ companyId: user.companyId }));
  }, [user?.companyId, user?.isCompanyAdmin]);

  const refreshDepartments = async (companyId) => {
    try {
      setDepartments(await listCompanyDepartments(companyId));
    } catch (e) {
      setDepartments([]);
    }
  };

  useEffect(() => {
    if (!user?.companyId || !user?.isCompanyAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getTenantConfig();
        if (!cancelled) setDeptScoping(!!cfg?.departmentScopingEnabled);
      } catch (e) {
        if (!cancelled) setDeptScoping(false);
      }
      if (!cancelled) await refreshDepartments(user.companyId);
    })();
    return () => { cancelled = true; };
  }, [user?.companyId, user?.isCompanyAdmin]);

  const UserListProperties = createSelector(
    (state) => state.User,
    (users) => ({
      error: users.error,
      users: users.users
    })
  );

  const {
    error,
    users
  } = useSelector(UserListProperties);

  const filteredUsers = (users || [])
    .filter((u) => {
      if (!deptFilter) return true;
      // Sentinel for the "no department yet" bucket — those users are invisible to any
      // department-scoped view, so admins need a way to find and fix them.
      if (deptFilter === UNASSIGNED_DEPARTMENT) return !u.department;
      return (u.department || "").toLowerCase() === deptFilter.toLowerCase();
    })
    .filter((u) => {
      const q = userSearch.trim().toLowerCase();
      if (!q) return true;
      return ["name", "email", "department", "phone"].some((k) => (u[k] || "").toLowerCase().includes(q));
    })
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const handleEditUserClick = (user) => {
    setCurrentUser({
      user_id: user.id,
      name: user.name,
      short_code: user.short_code,
      phone: user.phone,
      role_id: user.role_id,
      department: user.department || "",
      original_phone: user.phone,
    });
    toggleEditUserModal();
  };


  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    if (String(currentUser.name).trim().split(/\s+/).filter(Boolean).length < 2) {
      Swal.fire("Error", "Please enter both a first and last name.", "error");
      return;
    }
    if (deptEnabled && !String(currentUser.department || "").trim()) {
      Swal.fire("Error", "Please choose a department.", "error");
      return;
    }
    try {
      const { original_phone, ...payload } = currentUser;
      await editChildUser(currentUser.user_id, {
        ...payload,
        phone: payload.phone === original_phone ? undefined : payload.phone,
      });
      Swal.fire("Success", "User details updated successfully!", "success");
      toggleEditUserModal();
      dispatch(getUsers({ companyId: user?.companyId }));
      if (deptEnabled) refreshDepartments(user?.companyId);
    } catch (error) {
      Swal.fire("Error", apiErrorMessage(error, "Failed to update user. Please try again."), "error");
    }
  };

  const onChangePagination = (
    { page, limit }, filters, sorter) => {
    setPagination({ ...pagination, page: page ?? pagination.page, pageSize: limit ?? pagination.pageSize })
  }

  const handleDeleteUser = async (userId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteChildUser(user?.companyId, userId);
        Swal.fire("Deleted!", "User has been removed.", "success");
        dispatch(getUsers({ companyId: user?.companyId }));
      } catch (error) {
        Swal.fire("Error", "Failed to delete user. Please try again.", "error");
      }
    }
  };

  const CombinedProperties = createSelector(
    (state) => state.BulkList,
    (state) => state.ContactList,
    (state) => state.Config,
    (bulkList, contactList, config) => ({

      shortcodes: config.shortcodes,

    })
  );

  const { shortcodes } = useSelector(CombinedProperties);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Account" breadcrumbItem="Profile" />

          {/* Account — single card: header + tabs + content, divided by the tab border */}
          <Card>
            <CardBody className="pb-0 pt-4 px-4">
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-2">
                <div>
                  <h3 className="mb-2 fw-bold d-flex align-items-center flex-wrap gap-2">
                    {user?.User?.name || user?.companies?.[0]?.name || "Account"}
                    <Badge color="success" pill className="font-size-13 align-middle">
                      {user?.User?.status === "1" ? "Active" : "Inactive"}
                    </Badge>
                  </h3>
                  <div className="text-muted" style={{ fontSize: "0.95rem" }}>
                    <span className="me-3"><i className="mdi mdi-email-outline me-1"></i>{user?.User?.email || "—"}</span>
                    <span><i className="mdi mdi-phone-outline me-1"></i>{user?.User?.phone || "—"}</span>
                  </div>
                </div>
                <Button outline color="primary" onClick={toggleEditModal}>
                  <i className="mdi mdi-pencil me-1"></i> Edit
                </Button>
              </div>

              <Nav tabs className="mt-4">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "overview" })}
                    onClick={() => setActiveTab("overview")}
                    style={{ cursor: "pointer" }}
                  >
                    Overview
                  </NavLink>
                </NavItem>
                {isAdmin && (
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "users" })}
                      onClick={() => setActiveTab("users")}
                      style={{ cursor: "pointer" }}
                    >
                      Users <Badge color="light" className="text-dark ms-1">{(users || []).length}</Badge>
                    </NavLink>
                  </NavItem>
                )}
              </Nav>
            </CardBody>

            <CardBody className="px-4 pt-4 pb-4">
              <TabContent activeTab={activeTab}>
                {/* Overview */}
                <TabPane tabId="overview">
                  <p className="text-muted text-uppercase mt-2 mb-4" style={{ fontSize: "0.8rem", letterSpacing: "0.04em" }}>
                    Account details
                  </p>
                  <Row>
                    {[
                      ["Account name", user?.User?.name || user?.companies?.[0]?.name],
                      ["Email", user?.User?.email],
                      ["Phone", user?.User?.phone],
                      ["Role", ROLES?.find((r) => r?.value == user?.User?.role_id)?.text],
                      ["Status", user?.User?.status === "1" ? "Active" : "Inactive"],
                      ["Units available", (user?.Client?.remaining_units || 0).toLocaleString()],
                      ["Units to date", (user?.Client?.total_allocated_units || 0).toLocaleString()],
                      ["Country", user?.country],
                    ].map(([label, value]) => (
                      <Col md={4} className="mb-4" key={label}>
                        <p className="text-muted mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                        <div className="fw-semibold" style={{ fontSize: "1.05rem" }}>{value || "—"}</div>
                      </Col>
                    ))}
                  </Row>
                  <hr />
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Button outline color="primary" onClick={toggleEditModal}>
                      Edit Profile <i className="mdi mdi-pencil"></i>
                    </Button>
                    <Button outline color="primary" onClick={togglePasswordModal}>
                      Change Password <i className="mdi mdi-lock"></i>
                    </Button>
                    <small className="text-muted ms-2">
                      <i className="mdi mdi-information-outline"></i> Units are managed by your account manager — contact them to top up.
                    </small>
                  </div>
                </TabPane>

                {/* Users */}
                <TabPane tabId="users">
                  <Row className="mb-3 align-items-center">
                    <Col md={8}>
                      <div className="d-flex flex-wrap gap-2">
                        <Input
                          style={{ maxWidth: 320, minWidth: 220, flex: "1 1 220px" }}
                          placeholder="Search users by name, email, department…"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />
                        {deptEnabled && (
                          <div style={{ minWidth: 300, flex: "1 1 300px", maxWidth: 380 }}>
                            <Select
                              inputId="department-filter"
                              isClearable
                              options={[
                                ...departments.map(asOption),
                                { value: UNASSIGNED_DEPARTMENT, label: "No department" },
                              ]}
                              value={
                                deptFilter
                                  ? deptFilter === UNASSIGNED_DEPARTMENT
                                    ? { value: UNASSIGNED_DEPARTMENT, label: "No department" }
                                    : asOption(deptFilter)
                                  : null
                              }
                              onChange={(opt) => setDeptFilter(opt?.value || "")}
                              placeholder="All departments"
                              styles={DEPT_SELECT_STYLES}
                            />
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={4} className="text-md-end mt-2 mt-md-0">
                      {deptEnabled && (
                        <Button
                          outline
                          color="primary"
                          className="me-2"
                          onClick={() => setManageDeptModal(true)}
                        >
                          <i className="mdi mdi-cog-outline"></i> Departments
                        </Button>
                      )}
                      <Button color="primary" onClick={handleNewUser}>
                        <i className="mdi mdi-plus"></i> New User
                      </Button>
                    </Col>
                  </Row>
                  {deptFilter && (
                    <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                      Showing {filteredUsers.length} of {(users || []).length} users
                      {deptFilter === UNASSIGNED_DEPARTMENT
                        ? " with no department"
                        : ` in ${deptFilter}`}
                      .
                    </div>
                  )}
                  <TableContainer
                    columns={columns}
                    data={filteredUsers}
                    isGlobalFilter={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    pageSizeAtBottom={true}
                    paginationData={pagination}
                    onChangePagination={onChangePagination}
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers"
                    tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  />
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Container>
        <ManageDepartmentsModal
          isOpen={manageDeptModal}
          toggle={() => setManageDeptModal(false)}
          companyId={user?.companyId}
          departments={departments}
          onChanged={async () => {
            await refreshDepartments(user?.companyId);
            dispatch(getUsers({ companyId: user?.companyId }));
          }}
        />
        <Modal isOpen={modal} toggle={toggleModal} centered>
          <ModalHeader toggle={toggleModal}>Add New User</ModalHeader>
          <Form onSubmit={handleFormSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={newUser.name}
                  onChange={handleInputChange}
                  placeholder="Enter name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="phone">Phone</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  value={newUser.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  required
                />
              </FormGroup>
              {deptEnabled && (
                <DepartmentField
                  id="new-user-department"
                  value={newUser.department}
                  departments={departments}
                  onChange={(v) => setNewUser((prev) => ({ ...prev, department: v }))}
                />
              )}
              <FormGroup>
                <Label for="new-user-role">Role</Label>
                <Select
                  inputId="new-user-role"
                  options={ROLE_OPTIONS}
                  value={ROLE_OPTIONS.find((r) => String(r.value) === String(newUser.role_id)) || null}
                  onChange={(opt) => setNewUser((prev) => ({ ...prev, role_id: opt?.value ?? "" }))}
                  placeholder="Select a role"
                  styles={DEPT_SELECT_STYLES}
                />
              </FormGroup>
              <small className="text-muted d-block mb-2">
                A temporary password is emailed to the new user automatically.
              </small>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Add User
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
        <ChangePasswordModal visible={passwordModal} toggle={togglePasswordModal} />
        <Modal isOpen={editModal} toggle={toggleEditModal} centered>
          <ModalHeader toggle={toggleEditModal}>Edit Profile</ModalHeader>
          <Form onSubmit={handleEditSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="name">Account Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={currentUser.name}
                  onChange={(e) =>
                    setCurrentUser({ ...currentUser, name: e.target.value })
                  }
                  placeholder="Enter Account Name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  placeholder="Enter name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="phone">Phone</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  value={editFormData.phone}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                  required
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleEditModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Save Changes
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
        <Modal isOpen={editUserModal} toggle={toggleEditUserModal} centered>
          <ModalHeader toggle={toggleEditUserModal}>Edit User</ModalHeader>
          <Form onSubmit={handleEditUserSubmit}>
            <ModalBody>
              <FormGroup>
                <Label for="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={currentUser.name}
                  onChange={(e) =>
                    setCurrentUser({ ...currentUser, name: e.target.value })
                  }
                  placeholder="Enter name"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label for="phone">Phone</Label>
                <Input
                  type="text"
                  id="phone"
                  name="phone"
                  value={currentUser.phone}
                  onChange={(e) =>
                    setCurrentUser({ ...currentUser, phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                  required
                />
              </FormGroup>
              {deptEnabled && (
                <DepartmentField
                  id="edit-user-department"
                  value={currentUser.department || ""}
                  departments={departments}
                  onChange={(v) => setCurrentUser((prev) => ({ ...prev, department: v }))}
                />
              )}
              <FormGroup>
                <Label for="edit-user-role">Role</Label>
                <Select
                  inputId="edit-user-role"
                  options={ROLE_OPTIONS}
                  value={ROLE_OPTIONS.find((r) => String(r.value) === String(currentUser.role_id)) || null}
                  onChange={(opt) => setCurrentUser((prev) => ({ ...prev, role_id: opt?.value ?? "" }))}
                  placeholder="Select a role"
                  styles={DEPT_SELECT_STYLES}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleEditUserModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Save Changes
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
        <Modal isOpen={assignModal} toggle={() => setAssignModal(false)} centered size="lg">
          <ModalHeader toggle={() => setAssignModal(false)}>
            Assign Contact Lists – {assigningUser?.name}
          </ModalHeader>

          <ModalBody>
            {loadingLists ? (
              <div className="text-center py-4">
                <i className="bx bx-loader bx-spin font-size-24"></i>
              </div>
            ) : (
              <FormGroup>
                <Label for="contactLists">Select Contact Lists (hold Ctrl/Cmd to multi-select)</Label>
                <Input
                  type="select"
                  multiple
                  id="contactLists"
                  style={{ height: "200px" }}
                  value={selectedListIds}
                  onChange={(e) => {
                    const opts = e.target.options;
                    const vals = [];
                    for (let i = 0; i < opts.length; i++) {
                      if (opts[i].selected) vals.push(opts[i].value);
                    }
                    setSelectedListIds(vals);
                  }}
                >
                  {allLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} (ID: {list.id})
                    </option>
                  ))}
                </Input>
                {assignedLists.length > 0 ? (
                  <div className="mt-2">
                    <small className="text-success">
                      <strong>Already assigned:</strong>
                    </small>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {assignedLists.map((list) => (
                        <span
                          key={list.id}
                          className="badge badge-soft-success font-size-11"
                        >
                          {list.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <small className="text-muted">No lists currently assigned.</small>
                )}
              </FormGroup>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={() => setAssignModal(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAssignSave}
              disabled={loadingLists || selectedListIds.length === 0}
            >
              Save Assignment
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
